"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { FiSearch, FiHeart, FiShoppingBag, FiUser } from "react-icons/fi";
import { useState } from "react";
import { useMiniCart } from "@/app/context/MiniCartContext";
import AuthModal from "../AuthModal";

interface NavLink {
  id: number;
  url: string;
  text: string;
}

export default function NavbarClassicSticky({
  links,
  logoText,
  logoUrl,
}: {
  links: NavLink[];
  logoText: string | null;
  logoUrl: string | null;
}) {
  const pathname = usePathname();
  const { open } = useMiniCart();
  const [authOpen, setAuthOpen] = useState(false);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <>
      {/* ================= NAVBAR ================= */}
      <header
        className="
          sticky top-0 z-50
          bg-[var(--nav-bg)]
          shadow-[var(--shadow-sm)]
        "
      >
        <nav className="max-w-7xl mx-auto h-16 px-6 relative flex items-center">
          {/* ================= LOGO (LEFT) ================= */}
          <div className="flex items-center">
            <Link href="/" className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt={logoText || "Logo"} className="h-7" />
              ) : (
                <span className="text-lg font-semibold text-[var(--title)]">
                  {logoText}
                </span>
              )}
            </Link>
          </div>

          {/* ================= CENTER LINKS ================= */}
          <ul
            className="
              absolute left-1/2 top-1/2
              -translate-x-1/2 -translate-y-1/2
              hidden md:flex items-center gap-10
            "
          >
            {links.map((link) => (
              <li key={link.id}>
                <Link
                  href={link.url}
                  className={`
                    text-sm font-medium
                    transition-colors duration-200
                    ${
                      isActive(link.url)
                        ? "text-[var(--text-1)]"
                        : "text-[var(--text-1)] hover:text-[var(--primary)]"
                    }
                  `}
                >
                  {link.text}
                </Link>
              </li>
            ))}
          </ul>

          {/* ================= RIGHT ACTIONS ================= */}
          <div className="ml-auto flex items-center gap-4">
            {/* Search */}
            <div
              className="
                hidden sm:flex items-center
                rounded-md
                px-3 h-9
                bg-[var(--bg-col-1)]
                shadow-[var(--shadow-sm)]
              "
            >
              <input
                placeholder="What are you looking for?"
                className="
                  bg-transparent outline-none
                  text-sm w-44
                  text-[var(--text-1)]
                  placeholder:text-[var(--text-muted)]
                "
              />
              <FiSearch className="text-[var(--text-1)] ml-2" />
            </div>

            {/* Wishlist */}
            <button
              className="
                text-[var(--text-1)]
                hover:text-[var(--primary)]
                transition-colors
              "
            >
              <FiHeart size={18} />
            </button>

            {/* Cart */}
            <button
              onClick={open}
              className="
                text-[var(--text-1)]
                hover:text-[var(--primary)]
                transition-colors
              "
            >
              <FiShoppingBag size={18} />
            </button>

            {/* Account */}
            <button
              onClick={() => setAuthOpen(true)}
              className="
                text-[var(--text-1)]
                hover:text-[var(--primary)]
                transition-colors
              "
            >
              <FiUser size={18} />
            </button>
          </div>
        </nav>
      </header>

      {/* ================= AUTH MODAL ================= */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onLoginSuccess={() => setAuthOpen(false)}
      />
    </>
  );
}
